import PropTypes from 'prop-types';
import { useTiltifyCampaigns } from '@hooks/useTiltifyCampaigns.js';
import useTiltifyConfig from '@hooks/useTiltifyConfig.js';
import { useCallback, useEffect, useMemo } from 'preact/hooks';

TiltifyCampaignSelector.propTypes = {
  onCampaignSelected: PropTypes.func,
  disabled: PropTypes.bool,
  allowRetired: PropTypes.bool,
  onCampaignRetired: PropTypes.func,
  onCampaignRemoved: PropTypes.func,
  showCampaignRetired: PropTypes.bool,
};

export default function TiltifyCampaignSelector({
  onCampaignSelected,
  disabled,
  allowRetired = false,
  onCampaignRetired = (_) => {},
  onCampaignRemoved = (_) => {},
  showCampaignRetired = false,
}) {
  const config = useTiltifyConfig();
  const { campaigns, campaignsLoading } = useTiltifyCampaigns({
    ...config,
    tiltifyAllowRetired: allowRetired,
  });

  const setSelectedCampaign = useCallback((e) => {
    const id = e.target.value;
    if (id !== null) {
      const campaign = campaigns.find((campaign) => campaign.id === id);
      if (campaign) {
        config.tiltifyCampaignID = id;
        config.tiltifyCampaignSlug = campaign.slug;
        config.tiltifyCampaignGoal = campaign.goal.value;
        config.tiltifyCampaignName = campaign.name;
        config.tiltifyPollID = '';

        localStorage.removeItem('tiltifyPollID');
        localStorage.setItem('tiltifyCampaignID', id);
        localStorage.setItem('tiltifyCampaignSlug', campaign.slug);
        localStorage.setItem('tiltifyCampaignGoal', campaign.goal.value);
        localStorage.setItem('tiltifyCampaignName', campaign.name);

        onCampaignSelected({
          slug: config.tiltifyCampaignSlug,
          id: config.tiltifyCampaignID,
          goal: config.tiltifyCampaignGoal,
          name: config.tiltifyCampaignName,
        });
      }
    }
  }, [campaigns, onCampaignSelected]);

  useEffect(() => {
    if (
      !campaigns ||
      campaignsLoading ||
      !config.tiltifyCampaignID ||
      config.tiltifyCampaignID.length === 0
    )
      return;

    const currentCampaign = campaigns.find(
      (c) => c.id === config.tiltifyCampaignID
    );

    if (!currentCampaign) {
      onCampaignRemoved(config.tiltifyCampaignID);
    } else {
      if (currentCampaign.status === 'retired' && !allowRetired) {
        onCampaignRetired(currentCampaign.id);
      }
    }
  }, [campaigns, campaignsLoading, config.tiltifyCampaignID, campaignsLoading, allowRetired]);

  const campaignOptions = useMemo(() => {
    if (!campaigns) return [];
    return campaigns
      .filter((campaign) => {
        if (campaign.status === 'retired') {
          return allowRetired;
        }
        return true;
      })
      .map((campaign) => {
        const campaignName =
          campaign.status !== 'retired'
            ? campaign.name
            : campaign.name + ' (Retired)';

        return (
          <option
            title={campaign.name}
            value={campaign.id}
            key={campaign.id}
            id={campaign.id}
            selected={campaign.id === config.tiltifyCampaignID}
          >
            {campaignName}
          </option>
        );
      });
  }, [campaigns, allowRetired, config.tiltifyCampaignID]);

  if (campaignsLoading || campaigns === undefined)
    return (
      <div className="min-h-32 flex flex-col">
        <label className="form-control w-full flex-grow">
          <div className="label">
            <span className="label-text">Fundraising Campaign</span>
          </div>
          <div className="select skeleton" />
          <div className="label">
            <span className="label-text text-error opacity-0">
              Your previous campaign has been retired
            </span>
          </div>
        </label>

        <div>
          <span className="text-sm text-gray-500">
            If you have no St. Jude campaigns listed here, make sure that you
            have them published in Tiltify
          </span>
        </div>
      </div>
    );

  return (
    <div className="min-h-32 flex flex-col">
      <label className="form-control w-full flex-grow">
        <div className="label">
          <span className="label-text">Fundraising Campaign</span>
        </div>
        <select
          disabled={disabled}
          className="select select-bordered w-full"
          onChange={setSelectedCampaign}
          value={
            config.tiltifyCampaignID && config.tiltifyCampaignID.length
              ? config.tiltifyCampaignID
              : null
          }
        >
          <option
            title="Please select a campaign"
            value="null"
            disabled
            selected={
              !config.tiltifyCampaignID || config.tiltifyCampaignID.length === 0
            }
          >
            Please select a campaign
          </option>
          {campaignOptions}
        </select>

        <div className="label">
          <span
            className={`label-text text-error ${showCampaignRetired ? 'opacity-100' : 'opacity-0'} transition-all`}
          >
            Your previous campaign has been retired
          </span>
        </div>
      </label>
      <div>
        <span className="text-sm text-gray-500">
          If you have no St. Jude campaigns listed here, make sure that you have
          them published in Tiltify
        </span>
      </div>
    </div>
  );
}
